import db from "../db/database";
import { CashRegister } from "../interfaces/cash-register";
import { markForDelete, markForSync } from "./syncService";
import { getTPVById } from "./tpvService";

const indexDB = db as any;

// Servicio para manejar las operaciones relacionadas con las cajas

// Agregar una nueva caja a la base de datos
export const addCaja = async (caja:any) : Promise<CashRegister> => {
	try {
		// Verificar si el nombre de la caja ya existe
		const existingCaja = await indexDB.cajas
			.where("nombre")
			.equals(caja.nombre)
			.first();

		if (existingCaja) {
			throw new Error("Ya existe una caja con ese nombre");
		}

		// Si se especificó un TPV, obtener su nombre
		let tpvNombre = null;
		if (caja.TPV) {
			const tpv = await getTPVById(caja.TPV);
			tpvNombre = tpv ? tpv.nombre : null;
		}

		// Agregar la caja a la base de datos
		const id = await indexDB.cajas.add({
			nombre: caja.nombre,
			TPV: caja.TPV || null,
			tpvNombre: tpvNombre,
			createdAt: new Date(),
			pendingSync: 1, // Marcar como pendiente de sincronización
			lastModified: new Date(),
			No: caja.No || null, // Guardar el No de Business Central si existe
			deleted: false,
		});

		return { id, ...caja, tpvNombre };
	} catch (error) {
		console.error("Error al agregar caja:", error);
		throw error;
	}
};

// Obtener todas las cajas
export const getAllCajas = async () : Promise<CashRegister[]> => {
	try {
		// Obtener solo cajas no marcadas como eliminadas
		const cajas = await indexDB.cajas.filter((caja:any) => !caja.deleted).toArray();
		return cajas;
	} catch (error) {
		console.error("Error al obtener cajas:", error);
		throw error;
	}
};

// Obtener una caja por ID local
export const getCajaById = async (id: number) : Promise<CashRegister> => {
	try {
		const caja = await indexDB.cajas.get(id);
		if (!caja || caja.deleted) {
			throw new Error("Caja no encontrada");
		}
		return caja;
	} catch (error) {
		console.error(`Error al obtener caja con ID ${id}:`, error);
		throw error;
	}
};

// Obtener una caja por No de Business Central
export const getCajaByNo = async (no:string) : Promise<CashRegister> => {
	try {
		const caja = await indexDB.cajas
			.where("No")
			.equals(no)
			.and((caja:any) => !caja.deleted)
			.first();

		if (!caja) {
			throw new Error("Caja no encontrada");
		}
		return caja;
	} catch (error) {
		console.error(`Error al obtener caja con No ${no}:`, error);
		throw error;
	}
};

export const getCajaByNoOrId = async (noOrId: string | number) : Promise<CashRegister[]> => {
	try {
		let caja = await indexDB.cajas.get(noOrId);
		if (!caja) {
			caja = await getCajaByNo(noOrId as string);
		}
		return caja;
	} catch (error) {
		console.error(`Error al obtener caja con No o ID ${noOrId}:`, error);
		throw error;
	}
};

// Actualizar una caja existente
export const updateCaja = async (id:number, cajaData:any) : Promise<CashRegister> => {
	try {
		const caja = await indexDB.cajas.get(id);
		if (!caja || caja.deleted) {
			throw new Error("Caja no encontrada");
		}

		// Si se actualizó el TPV, obtener su nombre
		let tpvNombre = caja.tpvNombre;
		if (cajaData.TPV !== undefined && cajaData.TPV !== caja.TPV) {
			if (cajaData.TPV) {
				const tpv = await getTPVById(cajaData.TPV);
				tpvNombre = tpv ? tpv.nombre : null;
			} else {
				tpvNombre = null;
			}
		}

		// Actualizar la caja
		await indexDB.cajas.update(id, {
			...cajaData,
			tpvNombre: tpvNombre,
			lastModified: new Date(),
		});

		// Marcar como pendiente de sincronización
		await markForSync("cajas", id);

		return await getCajaById(id);
	} catch (error) {
		console.error(`Error al actualizar caja con ID ${id}:`, error);
		throw error;
	}
};

// Eliminar una caja
export const deleteCaja = async (id:number) : Promise<boolean>=> {
	try {
		const caja = await indexDB.cajas.get(id);
		if (!caja) {
			throw new Error("Caja no encontrada");
		}

		// Marcar como eliminada y pendiente de sincronización
		await markForDelete("cajas", id);

		return true;
	} catch (error) {
		console.error(`Error al eliminar caja con ID ${id}:`, error);
		throw error;
	}
};
